import * as React from 'react';

import { debounce } from 'lodash';

import { ContextualHelp, Link, TextField, Typography2 } from '@coursera/cds-core';
import type { TextFieldProps } from '@coursera/cds-core';

import PartnerHelpLink from 'bundles/authoring/common/components/PartnerHelpLink';
import {
  BadFormat,
  InProgress,
  SlugTooLong,
  Unavailable,
  Valid,
} from 'bundles/teach-course/constants/SlugValidationState';
import type { SlugValidationState } from 'bundles/teach-course/constants/SlugValidationState';

import _t from 'i18n!nls/teach-course';

export const INVALID_SLUG_REGEX = /[^a-z0-9-]/;
const VALIDATION_DELAY = 500;
export const MAX_SLUG_LENGTH = 80; // characters

type Props = {
  onChange: (newSlug: string, isValidSlug: boolean) => void;
  prefix: string;
  onValidate: (value: string) => void;
  validateOnLoad?: boolean;
  validationState: SlugValidationState;
  value?: string;
  disabled?: boolean;
  maxLength?: number;
  helpMessage?: string;
};

type State = {
  validationState: SlugValidationState;
  slug: string;
  slugIsTouched: boolean;
};

class SlugEditor extends React.Component<Props, State> {
  static defaultProps = {
    disabled: false,
    maxLength: MAX_SLUG_LENGTH,
  };

  lazyValidate: typeof this.validate = () => {
    // Initialized in componentDidMount
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      validationState: props.validationState,
      slug: props.value || '',
      slugIsTouched: false,
    };
  }

  componentDidMount() {
    const { value, validateOnLoad, onValidate } = this.props;

    this.lazyValidate = debounce(this.validate, VALIDATION_DELAY);

    if (validateOnLoad && value) {
      this.setState({ validationState: new InProgress() });
      onValidate(value);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.validationState !== this.props.validationState) {
      this.setState({ validationState: nextProps.validationState });
    }

    if (!this.state.slugIsTouched && typeof nextProps.value === 'string') {
      this.setState({ slug: nextProps.value });
    }
  }

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = event.target.value;
    this.lazyValidate(newSlug);
    this.setState({
      slug: newSlug,
      slugIsTouched: true,
    });
  };

  validate = (value: string) => {
    const { maxLength, onChange, onValidate } = this.props;

    if (!value || value.length === 0 || INVALID_SLUG_REGEX.test(value)) {
      this.setState({ validationState: new BadFormat() });
      onChange('', false);
      onValidate(value);
      return;
    }

    if (maxLength && value.length > maxLength) {
      this.setState({ validationState: new SlugTooLong({ maxLength }) });
      onChange(value, false);
      onValidate(value);
      return;
    }

    if (value === this.props.value) {
      this.setState({ validationState: new Valid() });
      onChange(value, true);
      onValidate(value);
      return;
    }

    this.setState({ validationState: new InProgress() });
    onValidate(value);
  };

  static isValidationError = function (validationState: SlugValidationState) {
    return (
      validationState instanceof BadFormat ||
      validationState instanceof Unavailable ||
      validationState instanceof SlugTooLong
    );
  };

  static isValidationSuccess = function (validationState: SlugValidationState) {
    return validationState instanceof Valid;
  };

  renderDisabled() {
    const url = this.props.prefix + this.props.value;
    return (
      <Link variant="quiet" href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </Link>
    );
  }

  getSlugValidationTextfieldProps = (
    slugValidationState: SlugValidationState
  ): Partial<Pick<TextFieldProps, 'validationLabel' | 'validationStatus'>> => {
    if (slugValidationState instanceof Unavailable) {
      return {
        validationLabel: _t('This URL is already in use.'),
        validationStatus: 'error',
      };
    } else if (slugValidationState instanceof SlugTooLong) {
      return {
        validationLabel: _t('URL cannot be longer than #{numCharacters} characters.', {
          numCharacters: MAX_SLUG_LENGTH,
        }),
        validationStatus: 'error',
      };
    } else if (slugValidationState instanceof BadFormat) {
      return {
        validationLabel: _t('Lower-case letters, numbers, and dashes only.'),
        validationStatus: 'error',
      };
    } else if (slugValidationState instanceof Valid) {
      return {
        validationStatus: 'success',
      };
    } else {
      return {
        validationLabel: undefined,
        validationStatus: undefined,
      };
    }
  };

  // This component initializes blank, and is controlled until it is auto-populated for the first time, or typed into, then it becomes uncontrolled.
  // Abstract the logic into this convenience function
  getControlledOrUncontrolledTextfieldProp = (
    value: string | undefined
  ): Partial<{ key?: string } & Pick<TextFieldProps, 'defaultValue' | 'value'>> => {
    const { slug, slugIsTouched } = this.state;
    if (slugIsTouched) {
      // Keep using state, uncontrolled
      return {
        defaultValue: slug,
      };
    } else if (typeof value === 'string') {
      // Empty strings are fine as well
      return {
        // Make this an uncontrolled component, with the provided initial value
        defaultValue: value,
      };
    } else {
      // Make this a controlled component, with the blank value. It is allowed to be "controlled" to have a value, later.
      return {
        key: 'empty-url',
        value,
      };
    }
  };

  renderEnabled() {
    const { validationState, slug } = this.state;
    const { disabled, helpMessage, maxLength, prefix } = this.props;

    return (
      <TextField
        {...this.getControlledOrUncontrolledTextfieldProp(slug)}
        characterLimit={maxLength}
        disabled={disabled}
        inputProps={{
          'aria-labelledby': 'contextual-help-label',
        }}
        label={_t('URL')}
        onChange={this.handleChange}
        prefix={prefix}
        renderLabel={
          !helpMessage
            ? () => <React.Fragment />
            : () => (
                <ContextualHelp
                  label={
                    <Typography2 component="h3" variant="subtitleMedium">
                      {_t('URL')}
                    </Typography2>
                  }
                  helpToggleProps={{
                    'aria-label': _t('Information about the "URL" section'),
                  }}
                  tooltipProps={{
                    cta: <PartnerHelpLink articleId="206408335" linkProps={{ typographyVariant: 'subtitleMedium' }} />,
                    closeButtonProps: {
                      'aria-label': _t('Close information about the "URL" section'),
                    },
                  }}
                >
                  <Typography2 component="p">{helpMessage}</Typography2>
                </ContextualHelp>
              )
        }
        {...this.getSlugValidationTextfieldProps(validationState)}
      />
    );
  }

  render() {
    return <div className="rc-SlugEditor">{this.props.disabled ? this.renderDisabled() : this.renderEnabled()}</div>;
  }
}

export default SlugEditor;
